import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Card, Col, Form, Row } from "react-bootstrap";
import IOpportunity, { IOpportunityCreateOrUpdate, IOpportunityInitResult } from "models/sql/IOpportunity";
import OpportunityService from "services/OpportunityService";
import { generateLog, getBaseDomain } from "utilities/MiscUtilities";
import IDealer, { IDealerFilters } from "models/sql/IDealer";
import DealerService from "services/DealerService";
import IFireDepartment, { IFireDepartmentFilters } from "models/sql/IFireDepartment";
import FireDepartmentService from "services/FireDepartmentService";
import { formatDate } from "utilities/StringUtility";
import moment from "moment";
import IEquipment, { IEquipmentFilters } from "models/sql/IEquipment";
import EquipmentService from "services/EquipmentService";
import IHealthRiskAssessment, { IHealthRiskAssessmentFilters } from "models/sql/IHealthRiskAssessment";
import HealthRiskAssessmentService from "services/HealthRiskAssessmentService";
import CountryChooser from "components/CountryChooser";
import MarkdownEditor from "components/MarkdownEditor";
import ISalesRep, { ISalesRepFilters } from "models/sql/ISalesRep";
import SalesRepService from "services/SalesRepService";
import ProductPricingCalculator, { IProductListEntry } from "components/ProductPricingCalculator";
import DPSEntitySelector from "components/DPSEntitySelector";
import StateChooser from "components/StateChooser";
import { useBeforeUnload } from "utilities/UIUtilities";
import DetailFooter from "components/DetailFooter";
import IFireStation, { IFireStationFilters } from "models/sql/IFireStation";
import FireStationService from "services/FireStationService";

interface IOpportunitiesDetailProps {
    baseData?: IOpportunityInitResult;
}

const OpportunitiesDetail = (props: IOpportunitiesDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const { id }: any = useParams();
    const [searchParams] = useSearchParams();
    const defs = searchParams.get("defs") || "{}";
    const parsedDefaults = defs ? JSON.parse(defs) : undefined;
    const [dealer, setDealer] = useState<IDealer>();
    const [salesRep, setSalesRep] = useState<ISalesRep>();
    const [fireDepartment, setFireDepartment] = useState<IFireDepartment>();
    const [fireStation, setFireStation] = useState<IFireStation>();
    const [airCard, setAirCard] = useState<IEquipment>();
    const [hra, setHRA] = useState<IHealthRiskAssessment>();
    const [initialProductList, setInitialProductList] = useState<IProductListEntry[]>([]);
    const [initialPublicProductList, setInitialPublicProductList] = useState<IProductListEntry[]>([]);
    const [Opportunity, setOpportunity] = useState<IOpportunity>({
        ... {
            opportunityId: -1,
            created: undefined,
            notes: "",
            extraData: undefined,
            stage: "",
            country: "",
            state: "",
            fireDepartmentId: undefined,
            dealerId: undefined,
            meetingType: "",
            meetingStatus: "",
            meetingDate: undefined,
            pocprimaryTitle: "",
            pocprimaryName: "",
            pocprimaryEmail: "",
            pocprimaryPhone: "",
            pocsecondaryTitle: "",
            pocsecondaryName: "",
            pocsecondaryEmail: "",
            pocsecondaryPhone: "",
            poctertiaryTitle: "",
            poctertiaryName: "",
            poctertiaryEmail: "",
            poctertiaryPhone: "",
            productInfoJson: "",
            healthRiskAssessmentId: undefined,
            healthRiskAssessmentSentDate: undefined,
            airCardId: undefined,
            airCardSentDate: undefined,
            fileAttachmentsJson: undefined,
            systemDataJson: undefined,
            lastUpdated: undefined,
            lastUpdatedBy: undefined,
            createdBy: undefined,
            fundingStatus: undefined,
            salesRepId: undefined,
            departmentName: undefined,
            station: undefined,
            totalValue: undefined,
            qualified: undefined,
            quotedTotalValue: 0,
            quoteStatus: "",
            code: "",
            source: "",
            timeline: undefined,
            likleyhood: undefined,
            closureDate: undefined,
            fireStationId: undefined
        }, ...(parsedDefaults ?? {})
    });
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);
    const [forceReload, setForceReload] = useState(new Date());
    const opportunityStages = ["Active Demo", "Closed Won", "Closed Lost", "Lead", "Open Won", "Order Imminent", "Qualify", "Quoting"];
    const meetingTypes = ["In-Person Meeting", "Not Applicable", "Virtual Meeting"];
    const meetingStatuses = ["Cancelled", "Completed", "Not Applicable", "Pursuing", "Scheduled"];
    const fundingStatuses = ["Funded", "Seeking Funding", "Unfunded"];
    const quoteStatuses = ["Accepted", "Requested", "Quoted"];
    const likelyhoods = ["10", "20", "30", "40", "50", "60", "70", "80", "90", "100"];
    const timelines = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page.");

    useEffect(
        () => {
            (async () => {
                if (id !== undefined) {
                    globalContextRef.current.setLoading(true);

                    const accessToken = await globalContextRef.current.getAccessTokenAsync();
                    const result = await OpportunityService.GetAsync(accessToken, id);

                    if (result.record) {
                        setOpportunity(result.record);

                        if (result.record.fireDepartmentId !== null && result.record.fireDepartmentId !== undefined) {
                            const departmentResult = await FireDepartmentService.GetAsync(accessToken, result.record.fireDepartmentId);

                            if (departmentResult.record) {
                                setFireDepartment(departmentResult.record);
                            }
                        }

                        if (result.record.dealerId !== null && result.record.dealerId !== undefined) {
                            const dealerResult = await DealerService.GetAsync(accessToken, result.record.dealerId);

                            if (dealerResult.record) {
                                setDealer(dealerResult.record);
                            }
                        }

                        if (result.record.airCardId !== null && result.record.airCardId !== undefined) {
                            const airCardResult = await EquipmentService.GetAsync(accessToken, result.record.airCardId);

                            if (airCardResult.record) {
                                setAirCard(airCardResult.record);
                            }
                        }

                        if (result.record.healthRiskAssessmentId !== null && result.record.healthRiskAssessmentId !== undefined) {
                            const healthRiskAssessmentResult = await HealthRiskAssessmentService.GetAsync(accessToken, result.record.healthRiskAssessmentId);

                            if (healthRiskAssessmentResult.record) {
                                setHRA(healthRiskAssessmentResult.record);
                            }
                        }

                        if (result.record.salesRepId !== null && result.record.salesRepId !== undefined) {
                            const salesRepResult = await SalesRepService.GetAsync(accessToken, result.record.salesRepId);

                            if (salesRepResult.record) {
                                setSalesRep(salesRepResult.record);
                            }
                        }

                        if (result.record.fireStationId !== null && result.record.fireStationId !== undefined) {
                            const fireStationResult = await FireStationService.GetAsync(accessToken, result.record.fireStationId);

                            if (fireStationResult.record) {
                                setFireStation(fireStationResult.record);
                            }
                        }

                        if (result.record.systemDataJson) {
                            const systemData = JSON.parse(result.record.systemDataJson);
                            if (systemData.productList) {
                                setInitialProductList(systemData.productList);
                            }

                            if (systemData.publicProductList) {
                                setInitialPublicProductList(systemData.publicProductList);
                            }
                        }
                    }
                    else {
                        globalContextRef.current.showErrorMessage("Unable to load Opportunity");
                    }

                    globalContextRef.current.setLoading(false);
                }
                else {
                    if (Opportunity?.fireDepartmentId != null && Opportunity?.fireDepartmentId != undefined) {
                        globalContext.setLoading(true);
                        const accessToken = await globalContext.getAccessTokenAsync();
                        const departmentResult = await FireDepartmentService.GetAsync(accessToken, Opportunity.fireDepartmentId);

                        if (departmentResult.record) {
                            setFireDepartment(departmentResult.record);
                        }

                        globalContext.setLoading(false);
                    }
                }
            })();
        }, [id, forceReload]);

    const deleteAsync = async () => {
        if (Opportunity && Opportunity.opportunityId && window.confirm("Are you SURE you want to delete this?")) {
            globalContextRef.current.setLoading(true);
            const accessToken = await globalContextRef.current.getAccessTokenAsync();
            await OpportunityService.DeleteAsync(accessToken, Opportunity.opportunityId);
            globalContextRef.current.setLoading(false);
            navigate("/Opportunities");
        }
    };

    const submitFormAsync = async () => {
        if (Opportunity) {
            globalContextRef.current.setLoading(true);
            const accessToken = await globalContextRef.current.getAccessTokenAsync();

            const data: IOpportunityCreateOrUpdate = {
                opportunityId: Opportunity.opportunityId,
                created: Opportunity.created,
                notes: Opportunity.notes,
                extraData: Opportunity.extraData,
                stage: Opportunity.stage,
                country: Opportunity.country,
                state: Opportunity.state,
                fireDepartmentId: Opportunity.fireDepartmentId,
                dealerId: Opportunity.dealerId,
                meetingType: Opportunity.meetingType,
                meetingStatus: Opportunity.meetingStatus,
                meetingDate: Opportunity.meetingDate,
                pocprimaryTitle: Opportunity.pocprimaryTitle,
                pocprimaryName: Opportunity.pocprimaryName,
                pocprimaryEmail: Opportunity.pocprimaryEmail,
                pocprimaryPhone: Opportunity.pocprimaryPhone,
                pocsecondaryTitle: Opportunity.pocsecondaryTitle,
                pocsecondaryName: Opportunity.pocsecondaryName,
                pocsecondaryEmail: Opportunity.pocsecondaryEmail,
                pocsecondaryPhone: Opportunity.pocsecondaryPhone,
                poctertiaryTitle: Opportunity.poctertiaryTitle,
                poctertiaryName: Opportunity.poctertiaryName,
                poctertiaryEmail: Opportunity.poctertiaryEmail,
                poctertiaryPhone: Opportunity.poctertiaryPhone,
                productInfoJson: Opportunity.productInfoJson,
                healthRiskAssessmentId: Opportunity.healthRiskAssessmentId,
                healthRiskAssessmentSentDate: Opportunity.healthRiskAssessmentSentDate,
                airCardId: Opportunity.airCardId,
                airCardSentDate: Opportunity.airCardSentDate,
                fileAttachmentsJson: Opportunity.fileAttachmentsJson,
                systemDataJson: Opportunity.systemDataJson,
                lastUpdated: Opportunity.lastUpdated,
                lastUpdatedBy: Opportunity.lastUpdatedBy,
                createdBy: Opportunity.createdBy,
                fundingStatus: Opportunity.fundingStatus,
                salesRepId: Opportunity.salesRepId,
                departmentName: Opportunity.departmentName,
                station: Opportunity.station,
                totalValue: Opportunity.totalValue,
                qualified: Opportunity.qualified,
                quotedTotalValue: Opportunity.quotedTotalValue,
                quoteStatus: Opportunity.quoteStatus,
                code: Opportunity.code,
                source: Opportunity.source,
                likleyhood: Opportunity.likleyhood,
                timeline: Opportunity.timeline,
                closureDate: Opportunity.closureDate,
                fireStationId: Opportunity.fireStationId
            };
            await OpportunityService.CreateOrUpdateAsync(accessToken, data);
            globalContextRef.current.setLoading(false);
            setHasFormDataChanged(false);

            if (data.opportunityId === -1) {
                navigate(-1);
            }
            else {
                setForceReload(new Date());
            }
        }
    };

    const calcScore = (hra: IHealthRiskAssessment) => {
        const total = (
            (hra?.stationCommonAreasScore || 0) +
            (hra?.stationLocationFactorScore || 0) +
            (hra?.stationPpegearScore || 0) +
            (hra?.incidentEquipmentHandlingScore || 0) +
            (hra?.incidentGrossDeconScore || 0) +
            (hra?.incidentProtectiveEquipmentScore || 0) +
            (hra?.apparatusPpegearScore || 0) +
            (hra?.apparatusVehicleDeconScore || 0) +
            (hra?.apparatusVehicleFactorsScore || 0) +
            (hra?.teamHealthWellnessScore || 0) +
            (hra?.teamPovuseScore || 0) +
            (hra?.teamRiskAwarenessScore || 0));

        return (
            Math.ceil((total / ((12 * 3) * 3)) * 100) + "%"
        );
    };

    if (Opportunity) {
        return (
            <>
                <Row>
                    <Col>
                        <Card.Body>
                            <Card.Title>
                                {
                                    id !== undefined &&
                                    <>
                                        <strong>{Opportunity.fireDepartment?.fireDeptName ?? "No Department Assigned"}</strong><br />
                                        {
                                            Opportunity.code &&
                                            <a
                                                href={`https://${getBaseDomain()}/Public/BudgetaryQuote?code=${Opportunity.code}`}
                                                target="_blank">View Budgetary Quote</a>
                                        }
                                    </>
                                }
                                {
                                    id === undefined &&
                                    <strong>New Opportunity</strong>
                                }
                            </Card.Title>

                            <DPSEntitySelector<IFireDepartment, IFireDepartmentFilters>
                                entityFilterSearchPropName={"fireDeptNames"}
                                entityService={FireDepartmentService}
                                entityTypeName="Department"
                                defaultEntityFilter={{}}
                                selectedEntityRef={fireDepartment}
                                generateEntityTitle={(entry) => `${entry.fireDeptName} ${entry.hqcity} ${entry.hqstate}, ${entry.hqzip} ${entry.hqCountry}`}
                                onClickExternalHandler={(entry) => {
                                    if (entry) {
                                        window.open(`/DPS/Departments/Detail/${entry.fireDepartmentId}`);
                                    }
                                }}
                                onClickAddNewHandler={() => {
                                    window.open(`/DPS/Departments/Detail/?defs={"fireDeptName":"${Opportunity.departmentName ?? ""}","hqstate":"${Opportunity.state ?? ""}","hqCountry":"${Opportunity.country ?? ""}"}`);
                                }}
                                entityChangeHandler={(newEntry) => {
                                    setOpportunity({ ...Opportunity, fireDepartmentId: newEntry?.fireDepartmentId || undefined });
                                    setFireDepartment(newEntry);
                                    setHasFormDataChanged(true);
                                }}
                            />

                            <DPSEntitySelector<IFireStation, IFireStationFilters>
                                entityFilterSearchPropName={"names"}
                                entityService={FireStationService}
                                entityTypeName="Station"
                                defaultEntityFilter={{ departmentIds: fireDepartment ? [fireDepartment.fireDepartmentId] : undefined }}
                                selectedEntityRef={fireStation}
                                generateEntityTitle={(entry) => `${entry.name} ${entry.city} ${entry.state}, ${entry.postalCode} ${entry.country}`}
                                onClickExternalHandler={(entry) => {
                                    if (entry) {
                                        window.open(`/DPS/Station/Detail/${entry.fireStationId}`);
                                    }
                                }}
                                onClickAddNewHandler={() => {
                                    window.open(`/DPS/Station/Detail/?defs={"fireDepartmentId":"${Opportunity.fireDepartmentId ? Opportunity.fireDepartmentId : ""}","state":"${Opportunity.state ?? ""}","country":"${Opportunity.country ?? ""}"}`);
                                }}
                                entityChangeHandler={(newEntry) => {
                                    setOpportunity({ ...Opportunity, fireStationId: newEntry?.fireStationId || undefined });
                                    setFireStation(newEntry);
                                    setHasFormDataChanged(true);
                                }}
                            />

                            <DPSEntitySelector<IDealer, IDealerFilters>
                                entityFilterSearchPropName={"dealerNames"}
                                entityService={DealerService}
                                entityTypeName="Dealer"
                                defaultEntityFilter={{}}
                                selectedEntityRef={dealer}
                                generateEntityTitle={(entry) => entry.dealerName ?? ""}
                                onClickExternalHandler={(entry) => {
                                    if (entry) {
                                        window.open(`/DPS/Dealers/Detail/${entry.dealerId}`);
                                    }
                                }}
                                entityChangeHandler={(newEntry) => {
                                    setOpportunity({ ...Opportunity, dealerId: newEntry?.dealerId || undefined });
                                    setDealer(newEntry);
                                    setHasFormDataChanged(true);
                                }}
                            />

                            <DPSEntitySelector<IEquipment, IEquipmentFilters>
                                entityFilterSearchPropName={"serialNumbers"}
                                entityService={EquipmentService}
                                entityTypeName="AirCard"
                                defaultEntityFilter={{ productPrefixs: ["r01"] }}
                                selectedEntityRef={airCard}
                                generateEntityTitle={(entry) => entry.serialNumber ?? ""}
                                onClickExternalHandler={(entry) => {
                                    if (entry) {
                                        window.open(`/DPS/Aircards/Detail/${entry.equipmentId}`);
                                    }
                                }}
                                entityChangeHandler={(newEntry) => {
                                    setOpportunity({ ...Opportunity, airCardId: newEntry?.equipmentId || undefined });
                                    setAirCard(newEntry);
                                    setHasFormDataChanged(true);
                                }}
                            />

                            <DPSEntitySelector<IHealthRiskAssessment, IHealthRiskAssessmentFilters>
                                entityFilterSearchPropName={"names"}
                                entityService={HealthRiskAssessmentService}
                                entityTypeName="HRA"
                                defaultEntityFilter={{}}
                                selectedEntityRef={hra}
                                generateEntityTitle={(entry) => `${entry.userName} ${entry.userEmail}`}
                                onClickExternalHandler={(entry) => {
                                    if (entry) {
                                        window.open(`/DPS/HealthRiskAssessments/Detail/${entry.healthRiskAssessmentId}`);
                                    }
                                }}
                                entityChangeHandler={(newEntry) => {
                                    setOpportunity({ ...Opportunity, healthRiskAssessmentId: newEntry?.healthRiskAssessmentId || undefined });
                                    setHRA(newEntry);
                                    setHasFormDataChanged(true);
                                }}
                            />

                            <DPSEntitySelector<ISalesRep, ISalesRepFilters>
                                entityFilterSearchPropName={"names"}
                                entityService={SalesRepService}
                                entityTypeName="Rep"
                                defaultEntityFilter={{ dealerIds: dealer ? [dealer.dealerId] : undefined }}
                                selectedEntityRef={salesRep}
                                generateEntityTitle={(entry) => entry?.fullName ?? ""}
                                onClickExternalHandler={(entry) => {
                                    if (entry) {
                                        window.open(`/DPS/Reps/Detail/${entry.salesRepId}`);
                                    }
                                }}
                                entityChangeHandler={(newEntry) => {
                                    setOpportunity({ ...Opportunity, salesRepId: newEntry?.salesRepId || undefined });
                                    setSalesRep(newEntry);
                                    setHasFormDataChanged(true);
                                }}
                            />

                            <Row className="align-items-end">
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Created Date</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                readOnly
                                                value={formatDate(Opportunity.created)}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Created By</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                readOnly
                                                value={Opportunity.createdBy}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label>State</Form.Label>
                                        {
                                            (Opportunity.fireDepartment || Opportunity.fireStation) &&
                                            <>
                                                &nbsp;({Opportunity.fireStation ? `Station: ${Opportunity.fireStation.state}` : Opportunity.fireDepartment ? `Dept: ${Opportunity.fireDepartment.hqstate}` : ""})<br />
                                            </>
                                        }
                                        {
                                            <StateChooser
                                                onChange={newVal => {
                                                    setOpportunity({ ...Opportunity, state: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                defaultValue={Opportunity.state}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Country</Form.Label>
                                        {
                                            (Opportunity.fireDepartment || Opportunity.fireStation) &&
                                            <>
                                                &nbsp;({Opportunity.fireStation ? `Station: ${Opportunity.fireStation.country}` : Opportunity.fireDepartment ? `Dept: ${Opportunity.fireDepartment.hqCountry}` : ""})<br />
                                            </>
                                        }
                                        <CountryChooser
                                            onChange={newVal => {
                                                setOpportunity({ ...Opportunity, country: newVal });
                                                setHasFormDataChanged(true);
                                            }}
                                            defaultValue={Opportunity.country}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Meeting Type</Form.Label>
                                        {
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setOpportunity({ ...Opportunity, meetingType: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={Opportunity.meetingType}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    meetingTypes.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Meeting Status</Form.Label>
                                        {
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setOpportunity({ ...Opportunity, meetingStatus: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={Opportunity.meetingStatus}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    meetingStatuses.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Next Meeting Date</Form.Label>
                                        {
                                            <Form.Control
                                                type="date"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, meetingDate: e.target.value ? e.target.value : undefined });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.meetingDate ? moment(Opportunity.meetingDate).format('yyyy-MM-DD') : undefined}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Opportunity Stage</Form.Label>
                                        {
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setOpportunity({ ...Opportunity, stage: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={Opportunity.stage}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    opportunityStages.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Funding Status</Form.Label>
                                        {
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setOpportunity({ ...Opportunity, fundingStatus: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={Opportunity.fundingStatus}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    fundingStatuses.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Quote Status</Form.Label>
                                        {
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setOpportunity({ ...Opportunity, quoteStatus: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={Opportunity.quoteStatus}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    quoteStatuses.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        }
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>POC Primary Name</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, pocprimaryName: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.pocprimaryName}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>POC Primary Title</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, pocprimaryTitle: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.pocprimaryTitle}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>POC Primary Email</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, pocprimaryEmail: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.pocprimaryEmail}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>POC Primary Phone</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, pocprimaryPhone: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.pocprimaryPhone}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                            </Row>
                            {/*
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>POC Secondary Name</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, pocsecondaryName: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.pocsecondaryName}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>POC Secondary Title</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, pocsecondaryTitle: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.pocsecondaryTitle}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>POC Secondary Email</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, pocsecondaryEmail: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.pocsecondaryEmail}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>POC Secondary Phone</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, pocsecondaryPhone: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.pocsecondaryPhone}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>POC Tertiary Name</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, poctertiaryName: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.poctertiaryName}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>POC Tertiary Title</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, poctertiaryTitle: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.poctertiaryTitle}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>POC Tertiary Email</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, poctertiaryEmail: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.poctertiaryEmail}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>POC Tertiary Phone</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, poctertiaryPhone: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.poctertiaryPhone}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                            </Row>
                                    */}
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Department Name</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, departmentName: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.departmentName}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Station</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, station: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.station}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Code</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, code: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.code}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Source</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setOpportunity({ ...Opportunity, source: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={Opportunity.source}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Likleyhood of Closure</Form.Label>
                                        {
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setOpportunity({ ...Opportunity, likleyhood: parseInt(e.target.value || "0") });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={Opportunity.likleyhood}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    likelyhoods.map((s, index) => <option key={s} value={s}>{s}%</option>)
                                                }
                                            </Form.Select>
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Timeline for Closure</Form.Label>
                                        {
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setOpportunity({ ...Opportunity, timeline: parseInt(e.target.value || "0") });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={Opportunity.timeline}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    timelines.map((s, index) => <option key={s} value={s}>{s} Months</option>)
                                                }
                                            </Form.Select>
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Expected Closure Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            onChange={
                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setOpportunity({ ...Opportunity, closureDate: e.target.value ? e.target.value : undefined });
                                                    setHasFormDataChanged(true);
                                                }
                                            }
                                            value={Opportunity.meetingDate ? moment(Opportunity.closureDate).format('yyyy-MM-DD') : undefined}
                                        />
                                    </Form.Group>
                                </Col>
                                {/* 
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Qualified</Form.Label>
                                        {
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setOpportunity({ ...Opportunity, qualified: e.target.value == "true" });
                                            setHasFormDataChanged(true);
                                                }}
                                                value={Opportunity.qualified ? "true" : "false"}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        }
                                    </Form.Group>
                                </Col>
                                */}
                            </Row>
                            <Row>
                                {
                                    globalContextRef.current.roles.some(r => r === "Web.Admin") &&
                                    <>
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Last Updated</Form.Label>
                                                {
                                                    <Form.Control
                                                        type="text"
                                                        readOnly
                                                        value={formatDate(Opportunity.lastUpdated)}
                                                    />
                                                }
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Last Updated By</Form.Label>
                                                {
                                                    <Form.Control
                                                        type="text"
                                                        readOnly
                                                        value={Opportunity.lastUpdatedBy}
                                                    />
                                                }
                                            </Form.Group>
                                        </Col>
                                    </>
                                }
                            </Row>
                            {
                                Opportunity.healthRiskAssessment &&
                                <Row className="border" style={{ "backgroundColor": "#eee" }}>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>HRA Submitter Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly
                                                    value={Opportunity.healthRiskAssessment?.userName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>HRA Submitter Email</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly
                                                    value={Opportunity.healthRiskAssessment?.userEmail}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>HRA Source</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly
                                                    value={Opportunity.healthRiskAssessment?.source}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>HRA Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly
                                                    value={calcScore(Opportunity.healthRiskAssessment)}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                            }

                            {
                                Opportunity.airCard &&
                                <Row className="border" style={{ "backgroundColor": "#eee" }}>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>AirCard Submitter Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly
                                                    value={Opportunity.airCard?.registrationName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>AirCard Submitter Email</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly
                                                    value={Opportunity.airCard?.registrationEmail}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Aircard Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly
                                                    value={Opportunity.airCard?.score}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                            }
                            <Row>
                                <Col>
                                    <ProductPricingCalculator
                                        className="mt-4"
                                        title="Opportunity Value Estimator"
                                        rep={salesRep}
                                        dealer={dealer}
                                        includeDiscounts={true}
                                        initialProductList={initialProductList}
                                        onChange={newProductList => {
                                            let newSystemDataJSON = JSON.parse(Opportunity.systemDataJson || "{}");
                                            newSystemDataJSON.productList = newProductList;
                                            setOpportunity({ ...Opportunity, systemDataJson: JSON.stringify(newSystemDataJSON) });
                                            setHasFormDataChanged(true);
                                        }}
                                        onTotalChange={(newTotal, newProductList, hasChanged) => {
                                            if (hasChanged) {
                                                console.log("ok 1");
                                                let newSystemDataJSON = JSON.parse(Opportunity.systemDataJson || "{}");
                                                newSystemDataJSON.productList = newProductList;
                                                setOpportunity({ ...Opportunity, totalValue: newTotal, systemDataJson: JSON.stringify(newSystemDataJSON) });
                                                setHasFormDataChanged(true);
                                            }
                                        }}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <ProductPricingCalculator
                                        className="mt-4"
                                        title="Public Quote"
                                        rep={salesRep}
                                        dealer={dealer}
                                        includeDiscounts={true}
                                        initialProductList={initialPublicProductList}
                                        onChange={newProductList => {
                                            let newSystemDataJSON = JSON.parse(Opportunity.systemDataJson || "{}");
                                            newSystemDataJSON.publicProductList = newProductList;
                                            setOpportunity({ ...Opportunity, systemDataJson: JSON.stringify(newSystemDataJSON) });
                                            setHasFormDataChanged(true);
                                        }}
                                        onTotalChange={(newTotal, newProductList, hasChanged) => {
                                            if (hasChanged) {
                                                console.log("ok 2");
                                                let newSystemDataJSON = JSON.parse(Opportunity.systemDataJson || "{}");
                                                newSystemDataJSON.publicProductList = newProductList;
                                                setOpportunity({ ...Opportunity, quotedTotalValue: newTotal, systemDataJson: JSON.stringify(newSystemDataJSON) });
                                                setHasFormDataChanged(true);
                                            }
                                        }}
                                    />
                                </Col>
                            </Row>

                            {
                                globalContextRef.current.roles.some(r => r === "Web.Admin") &&
                                <Row>
                                    <Form.Group className="mt-4 mb-3" >
                                        <Form.Label>Admin Notes</Form.Label>
                                        <MarkdownEditor
                                            globalContextRef={globalContextRef.current}
                                            noun="Note"
                                            onChange={newVal => {
                                                setOpportunity({ ...Opportunity, notes: newVal });
                                                setHasFormDataChanged(true);
                                            }}
                                            value={Opportunity.notes || ""}
                                        />
                                    </Form.Group>
                                </Row>
                            }
                            {
                                globalContextRef.current.roles.some(r => r === "Web.Admin") &&
                                <Row className="align-items-end">
                                    <Col>
                                        <Card className="mb-2">
                                            <Card.Body>
                                                <Card.Title>
                                                    <strong>Log</strong><br />
                                                </Card.Title>
                                                {generateLog<IOpportunity>(Opportunity)}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            }
                        </Card.Body>
                    </Col>
                </Row >

                {
                    globalContextRef.current.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }
            </>
        );
    }
    else {
        return (
            <>
                <h1>There was an issue retreiving the Opportunity, please try again.</h1>
            </>
        );
    }
}

export default OpportunitiesDetail;
